import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import markdown from "highlight.js/lib/languages/markdown";
import python from "highlight.js/lib/languages/python";
import typescript from "highlight.js/lib/languages/typescript";

hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("js", javascript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("ts", typescript);
hljs.registerLanguage("json", json);
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("sh", bash);
hljs.registerLanguage("shell", bash);
hljs.registerLanguage("python", python);
hljs.registerLanguage("py", python);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("md", markdown);

export function highlightFence(lang: string | undefined, code: string): string {
  const trimmed = code.replace(/\n$/, "");
  const language = lang?.trim().toLowerCase() || "";
  if (language && hljs.getLanguage(language)) {
    try {
      return hljs.highlight(trimmed, { language, ignoreIllegals: true }).value;
    } catch {
      // fall through
    }
  }
  try {
    const auto = hljs.highlightAuto(trimmed);
    return auto.value;
  } catch {
    return escapeHtml(trimmed);
  }
}

/**
 * Highlight a short inline code snippet (no line numbers, no copy button).
 * Used by the `codespan` renderer in markdown.ts so backtick-wrapped code
 * segments also pick up syntax colors. Falls back to plain escape if
 * highlight.js cannot classify the snippet.
 */
export function highlightInline(code: string): string {
  const trimmed = code.replace(/\n$/, "").replace(/\n/g, " ");
  if (!trimmed) {
    return "";
  }
  try {
    return hljs.highlightAuto(trimmed).value;
  } catch {
    return escapeHtml(trimmed);
  }
}

/** Highlight code with line numbers rendered as a table. */
export function highlightFenceWithLineNumbers(lang: string | undefined, code: string): string {
  const trimmed = code.replace(/\n$/, "");
  const lines = trimmed.split("\n");
  // Don't show line numbers for trailing empty lines
  while (lines.length > 1 && lines[lines.length - 1] === "") {
    lines.pop();
  }
  const language = lang?.trim().toLowerCase() || "";
  const width = String(lines.length).length;

  const rows = lines.map((line, i) => {
    const highlighted = highlightSingleLine(language, line);
    return `<tr><td class="chat-code-block__line-num" style="width:${width}ch">${i + 1}</td><td class="chat-code-block__line">${highlighted}</td></tr>`;
  });

  return `<table class="chat-code-block__lines">${rows.join("")}</table>`;
}

function highlightSingleLine(language: string, line: string): string {
  if (language && hljs.getLanguage(language)) {
    try {
      return hljs.highlight(line, { language, ignoreIllegals: true }).value;
    } catch {
      // fall through
    }
  }
  try {
    return hljs.highlightAuto(line).value;
  } catch {
    return escapeHtml(line);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function encodeCopyPayload(text: string): string {
  if (typeof btoa === "function") {
    try {
      return btoa(unescape(encodeURIComponent(text)));
    } catch {
      return "";
    }
  }
  return "";
}

export function decodeCopyPayload(encoded: string): string {
  if (!encoded || typeof atob !== "function") {
    return "";
  }
  try {
    return decodeURIComponent(escape(atob(encoded)));
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// UX-R12 / JAISIU-3336: Auto-detect code runs that are not properly fenced
// (malformed fences with no closing delimiter, or 4-space / tab indented
// blocks). We pre-process the markdown string so that downstream `marked`
// sees valid fenced blocks; the renderer in markdown.ts already calls
// `hljs.highlightAuto` when a fence has no language hint.
// ---------------------------------------------------------------------------

const FENCE_OPEN_RE = /^(\s*)(`{3,}|~{3,})\s*([^\s`~]*)\s*$/;
const INDENT_RE = /^( {4,}|\t+)/;
// CommonMark list continuations use a 2-space indent on the line following
// the marker. A line that is just "  - foo" or "  1. foo" is a list item, not
// a code block. We use this regex to skip those so we never wrap a list as
// code.
const LIST_ITEM_RE = /^(\s*)([-*+]|\d+[.)])\s+/;

interface Segment {
  kind: "code" | "text";
  text: string;
}

/**
 * Split a markdown document into alternating fenced-code and text segments.
 * A valid fenced block starts a line with 3+ backticks or 3+ tildes and is
 * closed by a line with at least as many of the same fence character.
 */
export function splitMarkdownSegments(input: string): Segment[] {
  const segments: Segment[] = [];
  const lines = input.split("\n");
  let buffer: string[] = [];
  let inCode = false;
  let fenceChar: "`" | "~" | null = null;
  let fenceLen = 0;

  const flushText = () => {
    if (buffer.length === 0) return;
    const text = buffer.join("\n");
    buffer = [];
    segments.push({ kind: "text", text });
  };

  for (const line of lines) {
    if (!inCode) {
      const open = FENCE_OPEN_RE.exec(line);
      if (open) {
        flushText();
        fenceChar = open[2][0] as "`" | "~";
        fenceLen = open[2].length;
        inCode = true;
        buffer.push(line);
        continue;
      }
      buffer.push(line);
      continue;
    }
    // Inside a fence: closing line has same char repeated at least fenceLen
    const trimmed = line.replace(/^\s+/, "");
    const charClass = fenceChar === "`" ? "`" : "~";
    const closeMatch = new RegExp(`^${charClass}{${fenceLen},}\\s*$`).exec(trimmed);
    buffer.push(line);
    if (closeMatch) {
      const text = buffer.join("\n");
      buffer = [];
      segments.push({ kind: "code", text });
      inCode = false;
      fenceChar = null;
      fenceLen = 0;
    }
  }
  // Trailing buffer
  if (inCode) {
    const charRepeat = (fenceChar ?? "`").repeat(fenceLen || 3);
    const text = buffer.join("\n") + "\n" + charRepeat;
    segments.push({ kind: "code", text });
  } else if (buffer.length > 0) {
    segments.push({ kind: "text", text: buffer.join("\n") });
  }
  return segments;
}

/**
 * Walk the input once and return the message with a synthetic closing fence
 * appended if there is any unterminated fence opener anywhere in the
 * document. The closing fence matches the LAST such opener (consistent with
 * how a human would manually close the most recent fence). Properly closed
 * fences are left untouched.
 */
function closeMalformedFences(input: string): string {
  const lines = input.split("\n");
  let lastUnclosedFence: { char: string; len: number } | null = null;
  let fenceChar: "`" | "~" | null = null;
  let fenceLen = 0;
  for (const line of lines) {
    if (fenceChar === null) {
      const open = FENCE_OPEN_RE.exec(line);
      if (open) {
        fenceChar = open[2][0] as "`" | "~";
        fenceLen = open[2].length;
        lastUnclosedFence = { char: fenceChar, len: fenceLen };
      }
      continue;
    }
    // Inside a fence: close on matching line
    const trimmed = line.replace(/^\s+/, "");
    const charClass = fenceChar === "`" ? "`" : "~";
    const closeMatch = new RegExp(`^${charClass}{${fenceLen},}\\s*$`).exec(trimmed);
    if (closeMatch) {
      fenceChar = null;
      fenceLen = 0;
      lastUnclosedFence = null;
    }
  }
  if (!lastUnclosedFence) return input;
  // Trim any trailing blank lines so the appended closer starts a new line.
  const trimmed = input.replace(/\n+$/, "");
  return trimmed + "\n" + lastUnclosedFence.char.repeat(lastUnclosedFence.len) + "\n";
}

/**
 * Wrap consecutive ≥4-space / tab-indented lines as a fenced ``` block.
 * Skip blockquote lines ("> ...") and list continuations so we never wrap
 * legitimate CommonMark lists or quotes as code.
 */
function wrapIndentedBlocks(segment: string): string {
  const lines = segment.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const isIndented = INDENT_RE.test(line);
    const isBlockquote = /^\s*>/.test(line);
    const isListItem = LIST_ITEM_RE.test(line);
    if (!isIndented || isBlockquote || isListItem) {
      out.push(line);
      i++;
      continue;
    }
    // Collect an indented run. Allow blank lines inside the run (CommonMark
    // permits blank lines inside a fenced code block).
    const startIdx = i;
    const run: string[] = [];
    while (i < lines.length) {
      const cur = lines[i];
      if (cur.trim() === "") {
        run.push("");
        i++;
        continue;
      }
      if (INDENT_RE.test(cur) && !/^\s*>/.test(cur)) {
        run.push(cur.replace(INDENT_RE, ""));
        i++;
        continue;
      }
      break;
    }
    const nonEmpty = run.filter((l) => l.trim() !== "");
    if (nonEmpty.length >= 2) {
      out.push("```");
      out.push(...run);
      out.push("```");
    } else {
      // Not really a code block — emit the original lines verbatim.
      for (let k = startIdx; k < i; k++) {
        out.push(lines[k]);
      }
    }
  }
  return out.join("\n");
}

/**
 * Pre-process a markdown string so that code-shaped runs are rendered as
 * syntax-highlighted blocks even when the assistant forgets to close a fence
 * or uses 4-space indentation. Designed to be called before `marked.parse`.
 *
 * Public surface for unit tests.
 */
export function preprocessMarkdown(input: string): string {
  if (!input) return input;
  // First, auto-close any malformed fences at the document level. After this
  // step every valid fence is paired.
  let result = closeMalformedFences(input);
  // Then walk segments and wrap indented blocks inside non-code regions.
  const segments = splitMarkdownSegments(result);
  const out: string[] = [];
  for (const seg of segments) {
    if (seg.kind === "code") {
      out.push(seg.text);
      continue;
    }
    out.push(wrapIndentedBlocks(seg.text));
  }
  return out.join("\n");
}
